// @ts-nocheck
/**
 * Template V2 — element Overview presentation (item viewer Overview tab recipes).
 *
 * Independent of ElementLayout[] (canvas tiles). See Overview Recipes plan.
 */

import type { ValueSource } from './binding';
import type { LocalizedText } from './metadata';

/** Built-in Overview recipe ids (host-owned scaffolds). */
export type OverviewRecipeId =
  | 'productHero'
  | 'personCard'
  | 'documentCover'
  | 'statusLedger'
  | 'metricBrief'
  | 'titleTable';

/** Column split policy for recipes that support a media|body split. */
export type OverviewColumnSplit = 'auto' | 'stack' | 'split';

/** Image fit — same contract as ComponentNode image fit. */
export type OverviewImageFit = 'contain' | 'cover' | 'fill';

/** Size nudge within a recipe’s allowed band. */
export type OverviewHeroSize = 'sm' | 'md' | 'lg';

export type OverviewHeroSource =
  | {
      kind: 'itemImage';
      /** Preferred index into item.imageUris; fallback 0 then color background. */
      preferredIndex?: number;
    }
  | {
      kind: 'templateAsset';
      assetId: string;
    }
  | { kind: 'none' };

export interface OverviewHeroConfig {
  source: OverviewHeroSource;
  fit: OverviewImageFit;
  size?: OverviewHeroSize;
}

/**
 * One bindable content cell. Slot id (metric1, fact2, …) is placement only —
 * any allowed ValueSource/DataRef may fill it.
 */
export interface OverviewSlotAssignment {
  /**
   * Value — static, DataRef binding, or expression.
   * When kind is `static` and value is author-facing text, store LocalizedText.
   */
  source: ValueSource<unknown>;
  /**
   * Descriptor label. Prefer LocalizedText for authored copy.
   * ValueSource is for rare bound labels from data.
   */
  label?: LocalizedText | ValueSource<string>;
  /**
   * Unit / suffix after the value.
   * Static LocalizedText or ValueSource resolving from data.
   */
  unit?: LocalizedText | ValueSource<string>;
  /** Optional format hint for numbers/dates. */
  format?: string;
  emptyPolicy?: 'hide' | 'showPlaceholder';
}

/** Attached to ElementDefinition — independent of ElementLayout[]. */
export interface ElementOverviewPresentation {
  recipeId: OverviewRecipeId;
  columnSplit: OverviewColumnSplit;
  hero: OverviewHeroConfig;
  /** Slot id → assignment. Keys validated against the recipe’s slot catalog. */
  slots: Record<string, OverviewSlotAssignment>;
}

/** Slot display role within a recipe. */
export type OverviewSlotRole =
  | 'subtitle'
  | 'status'
  | 'highlight'
  | 'fact'
  | 'body'
  | 'action'
  | 'table';

export const OVERVIEW_RECIPE_IDS: readonly OverviewRecipeId[] = [
  'productHero',
  'personCard',
  'documentCover',
  'statusLedger',
  'metricBrief',
  'titleTable',
] as const;

export const OVERVIEW_COLUMN_SPLITS: readonly OverviewColumnSplit[] = [
  'auto',
  'stack',
  'split',
] as const;

export const OVERVIEW_IMAGE_FITS: readonly OverviewImageFit[] = [
  'contain',
  'cover',
  'fill',
] as const;

export const OVERVIEW_HERO_SIZES: readonly OverviewHeroSize[] = ['sm', 'md', 'lg'] as const;
